<template>
  <div class="introduction-page">
    <!-- 顶部背景横幅 -->
    <div class="page-banner"></div>

    <!-- 主体内容区 -->
    <div class="page-content">
      <!-- 面包屑 -->
      <div class="breadcrumb">
        <span class="breadcrumb-icon">🏠</span>
        <span class="breadcrumb-text">当前位置：<span class="breadcrumb-link" @click="goHome">首页</span> / 详情</span>
      </div>

      <div class="content-wrapper">
        <!-- 左侧菜单 -->
        <div class="sidebar">
          <div class="sidebar-title">{{ parentNavName }}</div>
          <ul class="sidebar-menu">
            <li 
              v-for="item in menuItems" 
              :key="item.id"
              class="menu-item" 
              :class="{ active: activeTab === item.id }"
              @click="handleMenuClick(item)"
            >
              <span>{{ item.name }}</span>
              <span class="arrow">›</span>
            </li>
          </ul>
        </div>

        <!-- 右侧内容 -->
        <div class="main-content">
          <div class="detail-content">
            <h1 class="content-title">{{ articleDetail.title || title || '山东省心理咨询行业协会' }}</h1>
            <div class="meta-info" v-if="articleDetail.author || author || articleDetail.create_time || create_time">
              <span v-if="articleDetail.author || author" class="author">{{ articleDetail.author || author }}</span>
              <span v-if="articleDetail.create_time || create_time" class="create-time">{{ articleDetail.create_time || create_time }}</span>
            </div>
            <div class="content-text" v-html="articleDetail.content || content || defaultContent"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import article from '@/api/article'
import sys from '@/api/index'
import { setDocumentTitle } from '@/utils/title'
import { DEFAULT_SHARE_IMAGE, setWechatShare } from '@/utils/wechatShare'

const router = useRouter()
const route = useRoute()

// 动态路由参数
const detailId = ref(null)
const articleDetail = ref({})
const activeTab = ref('')
const menuItems = ref([])
const parentNavName = ref('详情内容')

// 跳转到首页
const goHome = () => {
  router.push('/')
}

// 获取文章详情
const fetchDetailData = async (id) => {
  try {
    const res = await article.getDetail({ id })
    if (res && res.data) {
      articleDetail.value = res.data
      setDocumentTitle(articleDetail.value.title)
      setWechatShare({
        title: articleDetail.value.title,
        desc: articleDetail.value.description || articleDetail.value.content,
        imgUrl: DEFAULT_SHARE_IMAGE,
        link: window.location.href.split('#')[0]
      })
      // 如果详情返回了 cid，用于匹配默认选中的菜单项
      if (res.data.cid && menuItems.value.length > 0) {
        const matchedItem = menuItems.value.find(item => item.id === res.data.cid)
        if (matchedItem) {
          activeTab.value = matchedItem.id
        }
      }
    }
  } catch (error) {
    console.error('获取文章详情失败:', error)
  }
}

// 根据 ident 获取菜单数据
const getMenuByIdent = async (ident) => {
  try {
    const res = await sys.getList({ ident })
    if (res && res.code === 2000 && res.data && Array.isArray(res.data)) {
      if (res.data.length > 0) {
        // 将返回的数据映射为菜单项，使用路由参数中的 ident
        menuItems.value = res.data.map(item => ({
          name: item.name,
          id: item.id,
          ident: ident
        }))
        
        // 如果已经获取了详情数据，根据 cid 匹配默认选中
        if (articleDetail.value.cid) {
          const matchedItem = menuItems.value.find(item => item.id === articleDetail.value.cid)
          if (matchedItem) {
            activeTab.value = matchedItem.id
          } else {
            // 如果没有匹配到，默认选中第一项
            activeTab.value = menuItems.value[0].id
          }
        } else {
          // 默认选中第一项
          activeTab.value = menuItems.value[0].id
        }
      }
    }
  } catch (error) {
    console.error('获取菜单数据失败:', error)
  }
}

// ident 到路由名称的映射
// const identRouteMap = {
//   news: 'associationNews',
//   work: 'work',
//   document: 'documents',
//   service: 'service',
//   social: 'social',
//   training: 'training'
// }

// 处理菜单点击事件
const handleMenuClick = async (item) => {
  activeTab.value = item.id
  
  // 调用 user.article/getList 接口获取文章列表
  try {
    const res = await article.getList({ cid: item.id })
    if (res && res.code === 2000) {
      console.log('获取文章列表成功:', res.data)
      
      // 获取第一篇文章并更新路由参数
      const dataList = res.data?.data || []
      if (dataList.length > 0) {
        const firstArticleId = dataList[0].id
        // 更新路由参数，不刷新页面
        router.replace({ 
          name: 'introductionDetailPage', 
          params: { id: firstArticleId }, 
          query: { ident: item.ident } 
        })
        // 重新获取文章详情
        await fetchDetailData(firstArticleId)
      }
    }
  } catch (error) {
    console.error('获取文章列表失败:', error)
  }
}

// 获取路由参数
onMounted(async () => {
  detailId.value = route.params.id
 
  const ident = route.params.ident || route.query.ident
   console.log(ident)
  // 先获取菜单数据
  if (ident) {
    await getMenuByIdent(ident)
  }
  
  // 再获取详情数据，这样可以根据详情的 cid 匹配菜单选中项
  if (detailId.value) {
    await fetchDetailData(detailId.value)
  }
})

const defaultContent = ``
</script>

<style scoped>
.introduction-page {
  width: 100%;
  min-height: 100vh;
}

/* 顶部横幅 */
.page-banner {
  width: 100%;
  height: 210px;
  background: url('~@/assets/images/innerbanner.jpg') center center / cover no-repeat;
  position: relative;
}

/* 主体内容 */
.page-content {
  max-width: 1400px;
  margin: -83px auto 0;
  padding: 0px 20px 60px;
  position: relative;
}

/* 面包屑 */
.breadcrumb {
  height: 80px;
  display: flex;
  align-items: center;
  font-size: 14px;
  background-color: #eaf7fa;
  padding: 10px 20px;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.breadcrumb-icon {
  margin-right: 8px;
  font-size: 16px;
  color: #4facfe;
}

.breadcrumb-text {
  color: #666;
  font-size: 1rem;
}

.breadcrumb-link {
  color: #666;
  cursor: pointer;
  transition: color 0.3s ease;
}

.breadcrumb-link:hover {
  color: #4facfe;
}

.content-wrapper {
  display: flex;
}

/* 左侧菜单 */
.sidebar {
  width: 240px;
  flex-shrink: 0;
  background-color: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  height: fit-content;
}

.sidebar-title {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: #fff;
  font-size: 18px;
  font-weight: 500;
  padding: 20px 24px;
  text-align: left;
  border-radius: 8px 8px 0 0;
}

.sidebar-menu {
  list-style: none;
  padding: 0;
  margin: 0;
}

.menu-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  color: #333;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 1px solid #f0f0f0;
  background-color: #fff;
}

.menu-item:last-child {
  border-bottom: none;
}

.menu-item:hover {
  background-color: #f8f9fa;
  color: #4facfe;
}

.menu-item.active {
  background-color: #f0f8ff;
  color: #4facfe;
  font-weight: 500;
}

.menu-item .arrow {
  font-size: 18px;
  color: #999;
}

.menu-item.active .arrow,
.menu-item:hover .arrow {
  color: #4facfe;
}

/* 右侧内容 */
.main-content {
  flex: 1;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  margin-left: 20px;
}

.detail-content {
  padding: 60px 80px;
}

.content-title {
  font-size: 32px;
  color: #333;
  margin-bottom: 30px;
  font-weight: 500;
  text-align: center;
  position: relative;
  padding-bottom: 20px;
}

.content-title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 3px;
  background: linear-gradient(to right, #4facfe, #00f2fe);
  border-radius: 2px;
}

.meta-info {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;
  gap: 8px;
  margin-bottom: 30px;
  font-size: 16px;
  color: #444;
}

.meta-info .author,
.meta-info .create-time {
  color: #444;
}

.content-text {
  color: #666;
  line-height: 2;
  font-size: 15px;
}

.content-text p {
  margin-bottom: 20px;
  text-align: justify;
  text-indent: 2em;
}

.content-text p:last-child {
  margin-bottom: 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .content-wrapper {
    flex-direction: column;
  }
  
  .sidebar {
    width: 100%;
  }
  
  .main-content {
    margin-left: 0;
    margin-top: 20px;
  }
  
  .detail-content {
    padding: 40px 20px;
  }
  
  .content-title {
    font-size: 24px;
  }
  
  .content-text {
    font-size: 14px;
  }
  
  .page-banner {
    height: 120px;
  }
}
</style>
